import React, { Component } from "react";
import { Navigate } from "react-router-dom";
import dataService from "../services/data.service";
import sorterService from "../services/sorter.service";

const DAILY_API_URL = "https://www.nasuomi.org/wp-json/paivan-teksti/v1/juuri-tanaan";

export default class Profile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      redirect: null,
      meetings: [],
      filteredMeetings: [],
      selectedCity: "",
      cityList: [],
      counter: 0,
      time: "",
      endtime: "",
      name: "",
      place: "",
      lisatiedot: "",
      mapsURL: "",
      daily: null,
      emptyMessage: "",
    };
  }

  async componentDidMount() {
    await Promise.allSettled([this.loadDaily(), this.loadMeetings()]);
  }

  // ------------------------------------------------------------
  // Load meetings from backend
  // ------------------------------------------------------------
  async loadMeetings() {
    try {
      const rows = await dataService.getMeetings();

      if (!rows.length) {
        this.setState({ emptyMessage: "Ei kokoustietoja saatavilla juuri nyt." });
        return;
      }

      const uniqueCities = [...new Set(rows.map(r => r.kaupunki).filter(Boolean))].sort();

      this.setState(
        {
          meetings: rows,
          cityList: uniqueCities,
          selectedCity: uniqueCities.includes("Tampere")
            ? "Tampere"
            : uniqueCities[0],
        },
        this.filterMeetings
      );
    } catch (error) {
      console.error("Meetings failed to load:", error);
      this.setState({
        meetings: [],
        cityList: [],
        emptyMessage: "Kokoustietoja ei voitu ladata. Yritä hetken päästä.",
      });
    }
  }

  // ------------------------------------------------------------
  // Load daily meditation text
  // ------------------------------------------------------------
  async loadDaily() {
    try {
      const response = await fetch(DAILY_API_URL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const formatted = (data.content || "").replace(/\n/g, "<br>");

      this.setState({
        daily: {
          date: data.date || "",
          title: data.title || "",
          content: formatted,
        },
      });
    } catch (error) {
      console.warn("Daily reading unavailable:", error);
    }
  }

  // ------------------------------------------------------------
  // Utility helpers
  // ------------------------------------------------------------
  buildMapsURL(place, city) {
    const q = [place || "", city || ""].filter(Boolean).join(", ");
    return (
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(q)
    );
  }

  calculateEndTime(start, duration) {
    if (!start) return "";
    const normalized = sorterService.normalizeTimeString(start);
    const [h, m] = normalized.split(":").map(Number);
    const totalMinutes = h * 60 + (m || 0) + (parseInt(duration) || 0);
    const endH = Math.floor(totalMinutes / 60) % 24;
    const endM = totalMinutes % 60;
    return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
  }

  // ------------------------------------------------------------
  // Dropdown change + filtering
  // ------------------------------------------------------------
  onCityChange = (event) => {
    this.setState({ selectedCity: event.target.value, counter: 0, emptyMessage: "" }, this.filterMeetings);
  };
  

  filterMeetings = () => {
    const { meetings, selectedCity } = this.state;
    if (!selectedCity || !meetings.length) return;
  
    const todayName = sorterService.todayName();
    const todayMeetings = sorterService.filterByDayName(meetings, todayName);
  
    // Filter by city + Internet
    const combined = todayMeetings.filter(
      (m) => m.kaupunki === selectedCity || m.kaupunki === "Internet"
    );
  
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
  
    // Keep meetings that haven't ended yet (either upcoming or ongoing)
    const sorted = sorterService
      .sortBySoonest(combined)
      .filter((m) => {
        const start = sorterService.timeToMinutes(m.alkamisaika);
        const dur = parseInt(m.kesto) || 0;
        const end = start + dur;
        return nowMinutes <= end; // keep if still upcoming or ongoing
      });
  
    if (!sorted.length) {
      this.setState({
        filteredMeetings: [],
        emptyMessage: "Ei ole enää kokouksia tänään, mene nukkumaan.",
        name: "",
        place: "",
        time: "",
        endtime: "",
        lisatiedot: "",
      });
      return;
    }
  
    const first = sorted[0];
    this.setMeetingDisplay(first, sorted);
  };
  

  setMeetingDisplay(meeting, sortedList) {
    this.setState({
      filteredMeetings: sortedList,
      counter: 0,
      name: meeting.title?.rendered || meeting.name || "",
      place: meeting.katuosoite || meeting.place || "",
      lisatiedot: meeting.lisatiedot || "",
      time: meeting.alkamisaika || meeting.hour || "",
      endtime: this.calculateEndTime(
        meeting.alkamisaika || meeting.hour,
        meeting.kesto
      ),
      mapsURL: meeting.karttalinkki
        ? meeting.karttalinkki
        : this.buildMapsURL(
            meeting.katuosoite || meeting.place,
            meeting.kaupunki
          ),
      emptyMessage: "",
    });
  }

  increment = () => {
    const { filteredMeetings, counter } = this.state;
    if (!filteredMeetings.length) return;
  
    const nextIdx = counter + 1;
  
    if (nextIdx < filteredMeetings.length) {
      // Go to next meeting normally
      const m = filteredMeetings[nextIdx];
      this.setMeetingDisplay(m, filteredMeetings);
      this.setState({ counter: nextIdx, emptyMessage: "" });
    } else {
      // Reached the last one → show "sleep" message but keep the button visible
      this.setState({
        emptyMessage: "Ei ole enää kokouksia tänään, mene nukkumaan.",
        // keep counter the same so button still shows
        name: "",
        place: "",
        time: "",
        endtime: "",
        lisatiedot: "",
        mapsURL: "",
      });
    }
  };
  
  
  

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  render() {
    const {
      cityList,
      selectedCity,
      name,
      place,
      time,
      endtime,
      lisatiedot,
      mapsURL,
      emptyMessage,
      daily,
    } = this.state;

    return (
      <div className="App-form">
        {/* City dropdown */}
        <div>
          <label htmlFor="citySelect">Valitse paikkakunta: </label>
          <select
            id="citySelect"
            value={selectedCity}
            onChange={this.onCityChange}
          >
            {cityList.map((c, i) => (
              <option key={i} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* If no meetings */}
        {emptyMessage && (
          <p>
            <b>{emptyMessage}</b>
          </p>
        )}

        {/* Meeting info */}
        {!emptyMessage && name && (
  <>
    <div className="time">
      <b>{time}</b> {endtime && <>– {endtime}</>}
    </div>
    <div>Nimi: <b>{name}</b></div>
    <div>Paikka: <b>{place}</b></div>
    <div>
      <a href={mapsURL} target="_blank" rel="noreferrer">Kartalla</a>
    </div>
    {/* Always show the button if there are any meetings loaded */}
{this.state.filteredMeetings.length > 0 && (
  <div>
    <button className="btn" onClick={this.increment}>
      {this.state.counter < this.state.filteredMeetings.length - 1
        ? "En kerkeä, näytä seuraava"
        : "Päivä pulkassa 😴"}
    </button>
  </div>
)}
    {lisatiedot && (
      <div
        className="meeting-extra"
        dangerouslySetInnerHTML={{ __html: lisatiedot }}
      />
    )}

    

  </>
)}


        {/* Daily text */}
        <div className="daily-container">
          {daily && (
            <div className="daily-content">
              {daily.date && <p className="daily-date">{daily.date}</p>}
              {daily.title && <h2 className="daily-title">{daily.title}</h2>}
              {daily.content && (
                <div dangerouslySetInnerHTML={{ __html: daily.content }} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
